import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DeviceMobile, DesktopTower, Laptop, ShieldCheck, ShieldSlash,
  Trash, PencilSimple, Check, X, Devices, Prohibit, Plus, ClockCounterClockwise, MapPin
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconFor = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('iphone') || n.includes('android')) return DeviceMobile;
  if (n.includes('ipad')) return Laptop;
  if (n.includes('mac') || n.includes('windows') || n.includes('linux')) return Laptop;
  return DesktopTower;
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
};

const formatAuditTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'Short' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day} ${month}, ${time}`;
  } catch {
    return iso;
  }
};

const RegisteredDevicesSection = () => {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [activeTab, setActiveTab] = useState('admin');

  // Login Audit state
  const [loginAudit, setLoginAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Blocked Devices / IPs state
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockType, setBlockType] = useState('IP');
  const [blockInput, setBlockInput] = useState('');
  const [ipLoading, setIpLoading] = useState(false);

  const currentDeviceId = typeof window !== 'undefined' ? localStorage.getItem('admin_device_id') : null;

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/devices`, { params: { admin_id: user.id, scope: 'all' } });
      setDevices(Array.isArray(data) ? data : []);
    } catch (e) {
      if (![401, 403].includes(e.response?.status)) {
        toast.error('Failed to load devices');
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const loadAudit = useCallback(async () => {
    if (user?.role !== 'super_admin') return;
    setAuditLoading(true);
    try {
      const { data } = await axios.get(`${API}/users/login-logs`, { params: { limit: 50 } });
      setLoginAudit(data?.logs || []);
    } catch (e) {
      console.error('Failed to load login audit:', e);
    } finally {
      setAuditLoading(false);
    }
  }, [user?.role]);

  const loadBlockedIps = useCallback(async () => {
    if (user?.role !== 'super_admin') return;
    try {
      const { data } = await axios.get(`${API}/settings/blocked-ips`);
      setBlockedIps(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load blocked IPs:', e);
    }
  }, [user?.role]);

  useEffect(() => {
    load();
    loadAudit();
    loadBlockedIps();
  }, [load, loadAudit, loadBlockedIps]);

  const toggleTrusted = async (dev) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/devices/${dev.id}`,
        { trusted: !dev.trusted },
        { params: { admin_id: user.id } }
      );
      setDevices((prev) => prev.map((d) => (d.id === dev.id ? { ...d, ...data } : d)));
      toast.success(data.trusted ? 'Device marked as trusted' : 'Trust revoked');
    } catch {
      toast.error('Failed to update device');
    }
  };

  const saveRename = async (dev) => {
    if (!editName.trim()) {
      setEditingId(null);
      return;
    }
    try {
      const { data } = await axios.patch(
        `${API}/admin/devices/${dev.id}`,
        { device_name: editName.trim() },
        { params: { admin_id: user.id } }
      );
      setDevices((prev) => prev.map((d) => (d.id === dev.id ? { ...d, ...data } : d)));
      setEditingId(null);
      toast.success('Renamed');
    } catch {
      toast.error('Failed to rename');
    }
  };

  const removeDevice = async (dev) => {
    const devLabel = dev.username ? `${dev.username}'s session (${dev.device_name || 'Browser'})` : dev.device_name || 'this device';
    if (!window.confirm(`Force log out / Kick "${devLabel}"? This active session will be terminated immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/devices/${dev.id}`);
      setDevices((prev) => prev.filter((d) => d.id !== dev.id));
      toast.success('Device session forcibly logged out (kicked)');
    } catch {
      toast.error('Failed to kick device session');
    }
  };

  const clearAuditHistory = async () => {
    if (!window.confirm('Clear all login audit logs?')) return;
    try {
      await axios.delete(`${API}/users/login-logs/clear`);
      setLoginAudit([]);
      toast.success('Login audit history cleared');
    } catch {
      toast.error('Failed to clear audit history');
    }
  };

  const handleBlock = async (e) => {
    e.preventDefault();
    if (!blockInput.trim()) return toast.error('Enter valid value to block');
    setIpLoading(true);
    try {
      const { data } = await axios.post(`${API}/settings/blocked-ips`, {
        ip_address: blockInput.trim(),
        reason: `Blocked ${blockType}`
      });
      toast.success(`${blockType} ${blockInput.trim()} blocked successfully`);
      setBlockInput('');
      setBlockedIps((prev) => [data, ...prev.filter(b => b.ip_address !== data.ip_address)]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to block');
    } finally {
      setIpLoading(false);
    }
  };

  const handleUnblock = async (id) => {
    try {
      await axios.delete(`${API}/settings/blocked-ips/${id}`);
      setBlockedIps((prev) => prev.filter((b) => b.id !== id && b.ip_address !== id));
      toast.success('Unblocked');
    } catch {
      toast.error('Failed to unblock');
    }
  };

  const adminDevices = devices.filter(
    (d) => d.user_role === 'super_admin' || d.user_role === 'admin' || (!d.user_role && user?.role === 'super_admin')
  );
  const handlerDevices = devices.filter(
    (d) => d.user_role && d.user_role !== 'super_admin' && d.user_role !== 'admin'
  );

  const displayedDevices = activeTab === 'admin' ? adminDevices : handlerDevices;

  return (
    <div className="space-y-6" data-testid="admin-registered-devices">
      {/* Registered Devices Card */}
      <div className="bg-wa-panel rounded-xl shadow p-5 border border-gray-150">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Devices size={22} weight="fill" className="text-purple-600" />
            <h3 className="text-lg font-semibold text-wa-text-primary">Registered Devices & Security</h3>
          </div>
          <button
            onClick={load}
            className="text-xs text-purple-600 hover:text-wa-text-primary font-medium"
            data-testid="devices-refresh-btn"
          >
            Refresh
          </button>
        </div>

        <p className="text-xs text-wa-text-secondary mb-4">
          Trusted devices skip Google verification on future logins. Only mark devices you personally use as trusted.
        </p>

        {/* Role Tabs: Admin vs Handlers */}
        <div className="flex items-center gap-3 border-b border-gray-200 mb-5">
          <button
            onClick={() => setActiveTab('admin')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'admin'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Admin ({adminDevices.length})
          </button>
          <button
            onClick={() => setActiveTab('handlers')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'handlers'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Handlers ({handlerDevices.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-wa-text-secondary">Loading devices...</div>
        ) : displayedDevices.length === 0 ? (
          <div className="text-center py-8 text-sm text-wa-text-secondary">No registered devices found for {activeTab === 'admin' ? 'Admins' : 'Handlers'}.</div>
        ) : (
          <div className="space-y-2">
            {displayedDevices.map((dev) => {
              const Icon = iconFor(dev.device_name);
              const isCurrent = dev.device_id === currentDeviceId;
              const isEditing = editingId === dev.id;
              return (
                <div
                  key={dev.id}
                  className={`border rounded-lg p-3 ${
                    dev.trusted ? 'border-green-400 bg-wa-hover/40' : 'border-wa-border'
                  }`}
                  data-testid={`device-row-${dev.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-wa-hover flex items-center justify-center">
                      <Icon size={22} weight="fill" className="text-wa-text-primary" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isEditing ? (
                          <input
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveRename(dev);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            className="border border-wa-border rounded px-2 py-0.5 text-sm w-40"
                            maxLength={80}
                            data-testid={`device-rename-input-${dev.id}`}
                          />
                        ) : (
                          <span className="font-medium text-wa-text-primary text-sm truncate">
                            {dev.device_name || 'Unnamed device'} {dev.username ? `(${dev.username})` : ''}
                          </span>
                        )}
                        {isCurrent && (
                          <span className="text-[10px] font-semibold bg-wa-hover text-wa-text-primary px-1.5 py-0.5 rounded uppercase tracking-wide">
                            This device
                          </span>
                        )}
                        {dev.trusted && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-wa-hover text-wa-text-primary px-1.5 py-0.5 rounded uppercase tracking-wide">
                            <ShieldCheck size={10} weight="fill" /> Trusted
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-wa-text-secondary mt-0.5 truncate">
                        {dev.ip_address || 'unknown IP'} · Last seen {formatTime(dev.last_seen)}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveRename(dev)}
                            className="p-1.5 text-green-600 hover:bg-wa-hover rounded"
                            title="Save"
                            data-testid={`device-rename-save-${dev.id}`}
                          >
                            <Check size={16} weight="bold" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1.5 text-wa-text-secondary hover:bg-wa-hover rounded"
                            title="Cancel"
                          >
                            <X size={16} weight="bold" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => { setEditingId(dev.id); setEditName(dev.device_name || ''); }}
                            className="p-1.5 text-wa-text-secondary hover:text-purple-600 hover:bg-wa-hover rounded"
                            title="Rename"
                            data-testid={`device-rename-btn-${dev.id}`}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => removeDevice(dev)}
                            className="p-1.5 text-red-600 hover:bg-wa-hover rounded"
                            title="Remove device"
                            data-testid={`device-remove-btn-${dev.id}`}
                          >
                            <Trash size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!isEditing && (
                    <button
                      onClick={() => toggleTrusted(dev)}
                      className={`mt-3 w-full py-2 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                        dev.trusted
                          ? 'bg-wa-hover text-wa-text-primary border border-orange-300 hover:bg-wa-hover'
                          : 'bg-wa-primary-strong text-white hover:bg-green-700'
                      }`}
                      data-testid={`device-trust-toggle-${dev.id}`}
                    >
                      {dev.trusted ? (
                        <>
                          <ShieldSlash size={16} weight="fill" />
                          Revoke Trust (require Google next time)
                        </>
                      ) : (
                        <>
                          <ShieldCheck size={16} weight="fill" />
                          Mark as Trusted (skip Google next time)
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Login Audit Card (Img 5) */}
      {user?.role === 'super_admin' && (
        <div className="bg-wa-panel rounded-xl shadow p-5 border border-gray-150 space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={22} weight="bold" className="text-purple-600" />
              <h3 className="text-lg font-bold text-gray-800">Login Audit</h3>
            </div>
            <button
              onClick={clearAuditHistory}
              className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-semibold cursor-pointer"
            >
              <Trash size={14} /> Clear history
            </button>
          </div>

          {auditLoading ? (
            <div className="text-center py-6 text-xs text-gray-400">Loading audit history...</div>
          ) : loginAudit.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-400">No login history records.</div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {loginAudit.map((log) => {
                const isFailed = log.status && log.status.includes('failed');
                const displayName = log.username || 'User';
                const roleText = log.role || 'user';
                return (
                  <div
                    key={log.id}
                    className={`border rounded-xl p-3.5 space-y-1.5 transition-colors ${
                      isFailed ? 'border-red-200 bg-red-50/20' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm text-gray-800">{displayName}</span>
                        <span className="text-xs text-gray-400">({roleText})</span>
                      </div>
                      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${
                        isFailed
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-green-50 text-green-700 border-green-200'
                      }`}>
                        {isFailed ? (log.status || 'failed - Incorrect password') : 'success'}
                      </span>
                    </div>

                    <div className="text-xs text-gray-500 font-medium">
                      {log.location || 'Unknown location'} · {log.ip_address || 'N/A'} · {formatAuditTime(log.login_time)}
                    </div>

                    {log.distance_from_prev_km && Number(log.distance_from_prev_km) > 0 && (
                      <div className={`flex items-center gap-1.5 text-xs font-bold mt-1 px-2.5 py-1 rounded border ${
                        Number(log.distance_from_prev_km) > 100
                          ? 'bg-amber-100 text-amber-900 border-amber-300'
                          : 'bg-amber-50 text-amber-700 border-amber-200'
                      }`}>
                        <MapPin size={14} weight="fill" className={Number(log.distance_from_prev_km) > 100 ? "text-amber-800" : "text-amber-600"} />
                        <span>
                          {Number(log.distance_from_prev_km) > 100 ? '⚠️ Far away login warning: ' : ''}
                          {Number(log.distance_from_prev_km).toFixed(1)} km from previous login location
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Blocked Devices / IPs Card (Img 5) */}
      {user?.role === 'super_admin' && (
        <div className="bg-wa-panel rounded-xl shadow p-5 border border-gray-150 space-y-4">
          <div className="border-b border-gray-100 pb-3">
            <h3 className="text-lg font-bold text-gray-800">Blocked Devices / IPs</h3>
          </div>

          <form onSubmit={handleBlock} className="flex flex-col sm:flex-row gap-2">
            <select
              value={blockType}
              onChange={(e) => setBlockType(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white font-medium outline-none"
            >
              <option value="IP">IP</option>
              <option value="Device ID">Device ID</option>
            </select>
            <input
              type="text"
              placeholder="e.g. 203.0.113.5"
              value={blockInput}
              onChange={(e) => setBlockInput(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none"
            />
            <button
              type="submit"
              disabled={ipLoading}
              className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
            >
              Block
            </button>
          </form>

          {blockedIps.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">Nothing blocked.</p>
          ) : (
            <div className="divide-y divide-gray-100 border rounded-lg overflow-hidden">
              {blockedIps.map((b) => (
                <div key={b.id} className="p-3 flex items-center justify-between hover:bg-gray-50 text-xs">
                  <div>
                    <span className="font-mono font-bold text-gray-800">{b.ip_address}</span>
                    <span className="text-gray-500 ml-2">({b.reason || 'Blocked'})</span>
                  </div>
                  <button
                    onClick={() => handleUnblock(b.id)}
                    className="px-2.5 py-1 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-md font-semibold transition-colors cursor-pointer"
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegisteredDevicesSection;

